import React, { useState } from 'react';
import Box from '@mui/material/Box';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { MenuProps } from '@mui/material/Menu';
import { Typography } from '@mui/material';
import { styled } from '@mui/system';
import { CareCreditQuickLinks } from '../../../../../../services/quick-actions-service/quick-actions-service';

const MenuItemStyled = styled(MenuItem)({
  display: 'flex',
  backgroundColor: 'white',
  justifyContent: 'space-between',
  padding: '8px 16px',
  height: 48,
});

const PatientLinksWrapper = styled(Box)({
  maxHeight: 500,
});

const NoScroll = styled(Menu)({
  overflow: 'hidden',
});

const CareCreditLinkList = styled(Box)({
  backgroundColor: 'white',
});

interface IProps {
  onSelect: (val: string, e: React.MouseEvent<HTMLLIElement, MouseEvent>) => void;
  quickLinks: CareCreditQuickLinks;
}

export default function CareCreditLinkQuickAction({ onSelect, quickLinks }: IProps) {
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuClose: MenuProps['onClose'] = (event) => {
    const e = event as React.MouseEvent<HTMLElement>;
    e.stopPropagation();
    setMenuAnchorEl(null);
  };

  const handleSelectLink = (link: string, e: React.MouseEvent<HTMLLIElement, MouseEvent>) => {
    onSelect(link || '', e);
    handleMenuClose(e, 'backdropClick');
  };

  const singleItemComponent = (title: string, link: string, key?: number) => {
    return (
      <MenuItemStyled
        key={key}
        data-testid="care-credit-quick-link"
        onClick={(e) => handleSelectLink(link, e)}
        sx={{
          backgroundColor: Boolean(menuAnchorEl) ? 'rgba(0, 0, 0, 0.08)' : 'white',
        }}
      >
        <Typography variant="body1">&#8288;{title}</Typography>
      </MenuItemStyled>
    );
  };

  const patientLinksComponent = (patientLinks: { fullName: string, link: string }[]) => {
    if (!patientLinks || patientLinks.length === 0) return null;
    if (patientLinks.length === 1) return singleItemComponent('Insert CareCredit Link', patientLinks[0].link);

    return (
      <>
        <MenuItemStyled
          data-testid="care-credit-quick-link"
          onClick={handleMenuOpen}
          sx={{
            backgroundColor: Boolean(menuAnchorEl) ? 'rgba(0, 0, 0, 0.08)' : 'white',
          }}
        >
          <Typography variant="body1">&#8288;Insert CareCredit Link</Typography>
          <ChevronRightIcon />
        </MenuItemStyled>
        <NoScroll
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
        >
          <CareCreditLinkList>
            <PatientLinksWrapper>
              {patientLinks.map((pl, index) => singleItemComponent(pl.fullName, pl.link, index))}
            </PatientLinksWrapper>
          </CareCreditLinkList>
        </NoScroll>
      </>
    );
  };

  if (!quickLinks || (!quickLinks.patientLinks && !quickLinks.locationLink)) return null;

  return (
    <>
      {quickLinks.locationLink && singleItemComponent('Insert CareCredit Link', quickLinks.locationLink)}
      {quickLinks.patientLinks && patientLinksComponent(quickLinks.patientLinks!)}
    </>
  );
}
